<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-5">
      <h4 class="mb-5">Let’s start your virtual job experience in hairdressing!</h4>
      <p class="mb-5">
        Hairdressers wash, cut, style, and colour clients’ hair. Basically, anything to do with hair
        care and appearance is within the hairdresser’s job description.
      </p>
      <p class="mb-5">
        For a hairdresser to do their job well, they’ll need to talk with their clients to find out
        precisely what it is they want. It helps to be social and enjoy talking to people in this
        job.
      </p>
      <p class="mb-5">
        Hairdressers need to keep their knowledge and skills up to date on the latest trends and
        styles. They’ll often refer to photographs and pictures in fashion and beauty magazines to
        keep track of the latest developments and develop new ideas for their clients.
      </p>
      <p class="mb-5">
        Hairdressing offers flexible ways of working. Some hairdressers work as full or part-time
        employees at a salon, while others prefer to freelance, work either at the home of clients
        or rent a chair at a salon.
      </p>
      <p class="mb-5">
        Many hairdressers go on to start their own business by opening up their own hair salon. Some
        hairdressers specialise in bridal hair, hair for fashion shows, or styling hair for TV and
        film.
      </p>
      <p class="mb-5">
        There are also opportunities in hairdressing to exercise your creative flair. You’ll be
        working with different colours, braids, textures, and various equipment. While some clients
        will be very prescriptive and know precisely what they want, others will leave a lot up to
        you to decide.
      </p>
    </vx-card>

    <vx-card title="Education">
      <p class="mb-5">
        You need a certificate III or IV in hairdressing or barbering to work as a Hairdresser.
        These courses are often completed as part of an apprenticeship.
      </p>
      <p class="mb-5">
        Before starting a course, check it will provide you with the skills and qualifications you
        need. <br />Visit
      </p>
      <p class="mb-5">
        <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare Vocational
        Education and Training (VET) courses, providers, and student outcomes.
      </p>
      <p class="mb-5">
        <a href="https://www.aapathways.com.au/career-research/job-pathways" target="_blank"
          >AAPathways</a
        >
        website to explore Hairdressing and Beauty VET training pathways.
      </p>
    </vx-card>

    <vx-card title="Quick Details">
      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
          <p class="mb-5"><strong>Size:</strong> This is a very large occupation.</p>
          <p class="mb-5">
            <strong>Earnings:</strong> Full-time workers on an adult wage earn around $1,025 per
            week (lower than the average of $1,460). Earnings tend to be lower when starting out and
            higher as experience grows.
          </p>
          <p class="mb-5">
            <strong>Full-time:</strong> Around half work full-time (51%, less than the average of
            66%), showing there are many opportunities to work part-time.
          </p>
          <p class="mb-5">
            <strong>Hours:</strong> Full-time workers spend around 41 hours per week at work
            (compared to an average of 44 hours).
          </p>
          <p class="mb-5">
            <strong>Age:</strong> The average age is 34 years (compared to the average of 40 years).
            Many workers are under 25 years of age (23%).
          </p>
          <p class="mb-5">
            <strong>Gender:</strong> 85% of workers are female (compared to the average of 48%).
          </p>

          <h4 class="mb-5">
            <u><strong>Tasks</strong></u>
          </h4>

          <ul class="default-list">
            <li>Providing advice on hair care, beauty products, and hairstyles</li>
            <li>Shampooing hair and conditioning scalps</li>
            <li>Colouring, straightening, and permanently waving hair with chemical solutions</li>
            <li>Cutting hair with scissors, clippers, and razors</li>
            <li>Styling hair into dreadlocks and braids and adding hair extensions</li>
            <li>Shaving and trimming beards and moustaches</li>
            <li>Cleaning work areas and sanitising instruments</li>
            <li>Arranging appointments and collecting payments</li>
            <li>May clean, colour, cut, and style wigs and hairpieces</li>
          </ul>
        </div>
        <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
          <img
            src="@/assets/images/experiences/hairdressing/jo1.png"
            class="mx-auto img-responsive"
            alt="Hairdressers job outlook: $1,025 weekly pay, strong future growth, 73,300 workers, medium skill level, 51% full-time, 41 hours average full-time week, average age 34, 85% female."
          />
          <p class="mb-10 text-center">
            Sourced from <a href="https://www.joboutlook.gov.au/" target="_blank">Job Outlook</a>
          </p>
        </div>
      </div>

      <vs-alert color="dark" title="Caution" active="true">
        The Australian jobs market is changing in response to the COVID-19 pandemic. These estimates
        do not take account of the impact of COVID-19. They may not reflect the current jobs market
        and should be used and interpreted with extreme caution.
      </vs-alert>
      <h4 class="my-5">Did You Know?</h4>
      <div class="vx-row mb-5">
        <div class="vx-col xl:w-1/3 lg:w-1/3 w-full mb-5">
          <p class="my-5">
            According to the Guinness Book of Records, the world's longest documented hair belongs
            to Xie Qiuping (China) at <strong>5.627 metres</strong> when measured on 8 May 2004.
          </p>
        </div>
        <div class="vx-col xl:w-2/3 lg:w-2/3 w-full my-5">
          <img
            src="@/assets/images/experiences/hairdressing/long_hair.jpg"
            alt="Xie Qiuping in a red dress with her hair trailing several metres across the ground behind her, with a Guinness World Records badge."
            class="mx-auto img-responsive"
          />
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import store from '@/store/store';

export default {
  data() {
    return {
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      videoTrigger1: require('@/assets/images/experiences/policing/police_officer_recruitment.jpg'),
      videoTrigger2: require('@/assets/images/experiences/policing/police_funny_moments.jpg'),
      videos: {
        1:
          $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
          '/content/policing/police_officer_recruitment.mp4',
        2:
          $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
          '/content/policing/police_funny_moments.mp4',
      },
    };
  },
  methods: {
    showVideoPopup(id) {
      store.dispatch(
        'popup/showVideoPopup',
        $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/policing/' + this.videos[id],
      );
    },
  },
};
</script>
